import { useState } from "react";

import { Alert } from "../../../components/ui/Alert";
import { Button } from "../../../components/ui/Button";
import { ConfirmDialog } from "../../../components/ui/overlay/ConfirmDialog";
import { materialCommand } from "../api/materialApi";
import type { Material } from "../schemas/materialSchemas";

type Command = "review" | "publish" | "withdraw";
const content: Record<Command, { label: string; description: string }> = {
  review: {
    label: "完成審查",
    description: "確認教材內容與檔案無誤，並將審查狀態標記為「已審查」。",
  },
  publish: {
    label: "發布教材",
    description: "發布後，學員便能在教材頁看到並下載此教材。",
  },
  withdraw: {
    label: "撤下教材",
    description: "撤下後，學員將無法再從教材頁看到或下載此教材。",
  },
};
export function MaterialWorkflow({
  material,
  onChanged,
}: {
  material: Material;
  onChanged: (value: Material) => void;
}) {
  const [command, setCommand] = useState<Command | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const available = material.currentVersion?.status === "AVAILABLE";
  const canReview =
    material.reviewStatus === "DRAFT" &&
    material.publicationStatus !== "PUBLISHED" &&
    available;
  const canPublish =
    material.reviewStatus === "REVIEWED" &&
    material.publicationStatus !== "PUBLISHED" &&
    available;
  const canWithdraw = material.publicationStatus === "PUBLISHED";
  const run = async () => {
    if (!command) return;
    setLoading(true);
    setError(undefined);
    try {
      onChanged(await materialCommand(material, command));
      setCommand(null);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "教材狀態更新失敗，請稍後再試。",
      );
      setCommand(null);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="border-top pt-3 mt-3">
      <div className="font-weight-bold mb-1">可執行操作</div>
      <p className="small text-muted mb-3">
        教材需先完成審查才能發布；已發布的教材可隨時撤下。
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="d-flex flex-wrap align-items-center">
        <Button
          variant="secondary"
          disabled={!canReview}
          onClick={() => setCommand("review")}
        >
          完成審查
        </Button>
        <span className="mx-2 text-muted" aria-hidden="true">
          →
        </span>
        <Button
          variant="success"
          disabled={!canPublish}
          onClick={() => setCommand("publish")}
        >
          發布教材
        </Button>
        {canWithdraw && (
          <Button
            className="ml-2"
            variant="danger"
            onClick={() => setCommand("withdraw")}
          >
            撤下教材
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={command !== null}
        title={command ? `確認${content[command].label}` : "確認教材狀態變更"}
        description={command ? content[command].description : ""}
        confirmLabel={command ? content[command].label : "確認"}
        destructive={command === "withdraw"}
        loading={loading}
        onConfirm={() => void run()}
        onClose={() => setCommand(null)}
      />
    </div>
  );
}
